import { useQuery } from 'react-query';
import { getPracticeChartInfo } from 'apis/chart/studentPracticeChart';
import { getEvaluationChartInfo } from 'apis/chart/studentEvaluationChart';
import { TPracticeChartInfoResponse } from 'types/apis/chart/studentPracticeChart';
import { TEvaluationChartInfoResponse } from 'types/apis/chart/studentEvaluationChart';
import useModal from 'hooks/useModal';
import useGoBack from 'hooks/useGoBack';
import { getEvaluationChartForFeedback } from 'apis/professor';

export type TChartInfoResponse =
  | TPracticeChartInfoResponse
  | TEvaluationChartInfoResponse;

type TProps = {
  category: string;
  chartType: number;
  id: number;
  patientId?: string | undefined;
  courseInfoId?: string | undefined;
  chartRound?: string | undefined;
  onSuccess: (data: TChartInfoResponse) => void;
};

const useGetChartInfo = ({
  category,
  chartType,
  id,
  patientId,
  courseInfoId,
  chartRound,
  onSuccess,
}: TProps) => {
  const { openModal } = useModal();
  const goBack = useGoBack();

  const goBackToList = () =>
    goBack({
      patientId,
      courseInfoId,
      chartRound,
    });

  const enableCondition =
    category !== 'evaluation'
      ? !!id
      : !!patientId && !!courseInfoId && !!chartRound;

  return useQuery(
    [
      'getChartInfo',
      category,
      chartType,
      id,
      patientId,
      courseInfoId,
      chartRound,
    ],
    async () => {
      if (!enableCondition) throw new Error('필수 정보가 누락되었습니다.');
      switch (category) {
        case 'practice': {
          const { data } = await getPracticeChartInfo(id, chartType);
          return data as TPracticeChartInfoResponse;
        }
        case 'feedback': {
          const { data } = await getEvaluationChartForFeedback(id);
          return data as TEvaluationChartInfoResponse;
        }
        default: {
          if (!patientId || !courseInfoId || !chartRound)
            throw new Error('필수 정보가 누락되었습니다.');
          const { data } = await getEvaluationChartInfo({
            patientId,
            courseInfoId,
            chartRound,
            chartTypeNum: chartType,
          });
          return data as TEvaluationChartInfoResponse;
        }
      }
    },
    {
      onSuccess,
      onError: () =>
        openModal({
          title: '차트 조회 실패',
          text: '다시 시도해 주세요.',
          onSubmit: goBackToList,
          onClickOutside: goBackToList,
        }),
    }
  );
};

export default useGetChartInfo;
